import type { ReactElement } from 'react';
import {
  ArrowDown,
  ArrowRight,
  ArrowUp,
  Ban,
  Banknote,
  CalendarClock,
  CalendarDays,
  Camera,
  Check,
  ChevronRight,
  CircleAlert,
  CircleCheckBig,
  CircleDashed,
  CircleDot,
  CirclePause,
  ClipboardCheck,
  Clock,
  Construction,
  Container,
  Crosshair,
  FileText,
  Forklift,
  Gauge,
  Handshake,
  HardHat,
  House,
  Layers,
  ListChecks,
  LockKeyhole,
  Map as MapIcon,
  MapPin,
  MapPinOff,
  Monitor,
  Moon,
  Package,
  Plus,
  Radio,
  Receipt,
  RefreshCw,
  Route,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  TowerControl,
  Sun,
  Tractor,
  TriangleAlert,
  Truck,
  Undo2,
  UserRound,
  Wrench,
  X,
  type LucideIcon,
} from 'lucide-react';
import { machineMotif as deriveMotif, type MachineMotif } from '@erm/api-client';

/**
 * The product's icon set, web side.
 *
 * The SAME family and the same names as the native app's
 * (`apps/mobile/src/components/icons.tsx`), so "requests" is one drawing across
 * both platforms rather than two people's idea of a handshake. Callers name a
 * product idea, never a component: swapping what an idea looks like is then one
 * line, everywhere.
 *
 * Size and stroke belong to the system. Three sizes and one weight, because an
 * icon set whose weights drift is the fastest way to make a page look assembled
 * rather than designed.
 *
 * Every icon in this product sits beside a word, so each is `aria-hidden` by
 * default and adds nothing to the accessibility tree; a caller with genuinely
 * no adjacent label passes `label` and gets an `img` role instead. `color:
 * currentColor` on the SVG means an icon inherits the ink of whatever it sits
 * in — which is what keeps it correct in dark mode without a second palette.
 */
export type IconName =
  | 'home'
  | 'fleet'
  | 'requests'
  | 'bookings'
  | 'account'
  | 'published'
  | 'paused'
  | 'draft'
  | 'onHire'
  | 'upcoming'
  | 'allClear'
  | 'attention'
  | 'arrow'
  | 'rateCard'
  | 'machine'
  | 'excavator'
  | 'crane'
  | 'forklift'
  | 'truck'
  | 'tractor'
  | 'container'
  | 'themeLight'
  | 'themeDark'
  | 'themeSystem'
  | 'browse'
  // ---- machine status (PRODUCT_UX_ARCHITECTURE.md §3) -------------------
  | 'available'
  | 'inTransport'
  | 'handover'
  | 'maintenance'
  | 'retired'
  // ---- the rental lifecycle (§5) ----------------------------------------
  | 'transport'
  | 'evidence'
  | 'meter'
  | 'billing'
  | 'returned'
  | 'money'
  // ---- map (§4) ----------------------------------------------------------
  | 'map'
  | 'location'
  | 'locationOff'
  | 'locate'
  // ---- people the platform does not yet model (§2.4, §15) ----------------
  | 'technician'
  // ---- state of a beat on the rail ---------------------------------------
  | 'stepDone'
  | 'stepCurrent'
  | 'stepUpcoming'
  // ---- controls ----------------------------------------------------------
  | 'search'
  | 'filter'
  | 'chevron'
  | 'close'
  | 'add'
  | 'check'
  | 'retry'
  | 'clock'
  | 'frozen'
  | 'blocked'
  | 'checklist'
  | 'trusted'
  | 'moveUp'
  | 'moveDown';

const GLYPHS: Record<IconName, LucideIcon> = {
  home: House,
  fleet: Layers,
  requests: Handshake,
  bookings: CalendarDays,
  account: UserRound,
  published: Radio,
  paused: CirclePause,
  draft: FileText,
  onHire: Truck,
  upcoming: CalendarClock,
  allClear: CircleCheckBig,
  attention: TriangleAlert,
  arrow: ArrowRight,
  rateCard: Wrench,
  machine: Package,
  excavator: Construction,
  crane: TowerControl,
  forklift: Forklift,
  truck: Truck,
  tractor: Tractor,
  container: Container,
  themeLight: Sun,
  themeDark: Moon,
  themeSystem: Monitor,
  browse: Search,

  available: CircleCheckBig,
  inTransport: Route,
  handover: ClipboardCheck,
  maintenance: Wrench,
  retired: Ban,

  transport: Route,
  evidence: Camera,
  meter: Gauge,
  billing: Receipt,
  returned: Undo2,
  money: Banknote,

  map: MapIcon,
  location: MapPin,
  locationOff: MapPinOff,
  locate: Crosshair,

  technician: HardHat,

  stepDone: CircleCheckBig,
  stepCurrent: CircleDot,
  stepUpcoming: CircleDashed,

  search: Search,
  filter: SlidersHorizontal,
  chevron: ChevronRight,
  close: X,
  add: Plus,
  check: Check,
  retry: RefreshCw,
  clock: Clock,
  frozen: LockKeyhole,
  blocked: CircleAlert,
  checklist: ListChecks,
  trusted: ShieldCheck,
  moveUp: ArrowUp,
  moveDown: ArrowDown,
};

export const ICON_SIZE = { sm: 16, md: 20, lg: 24, xl: 28 } as const;
export type IconSize = keyof typeof ICON_SIZE;

/** One weight, matching the native set's. */
const STROKE = 1.75;

export function Icon({
  name,
  size = 'md',
  label,
}: {
  name: IconName;
  size?: IconSize;
  /** Supply ONLY when no adjacent word says the same thing. */
  label?: string;
}): ReactElement {
  const Glyph = GLYPHS[name];
  const points = ICON_SIZE[size];

  return label === undefined ? (
    <Glyph
      size={points}
      strokeWidth={STROKE}
      className="icon"
      aria-hidden={true}
      focusable="false"
    />
  ) : (
    <Glyph
      size={points}
      strokeWidth={STROKE}
      className="icon"
      role="img"
      aria-label={label}
      focusable="false"
    />
  );
}

/*
 * `IconTile` — REMOVED, deliberately.
 *
 * It put a drawing on a tonal plate: an accent, success, warning, info or
 * neutral ground behind an icon. Four of those in a row on the owner's home
 * screen were the single largest source of the "random coloured icon
 * backgrounds" that made the product read as a template — the colour was
 * decoration, not meaning, and it spent the semantic palette on ornament until
 * a genuine warning had nothing left to say with.
 *
 * DESIGN_SYSTEM.md §1.2 now closes the list: semantic colour appears as a
 * status word, a 3px leading rule on an object in that state, inline text, or a
 * 6px dot beside its word. Never as a ground behind a glyph.
 *
 * An icon does not need a plate to be visible. It inherits the ink of whatever
 * it sits in, which is also what keeps it correct in dark mode without a second
 * palette.
 */

/**
 * Which drawing stands for a machine, from the words the owner typed.
 *
 * The RULE lives in `@erm/api-client` (`machineMotif`), not here. It used to
 * live in this file and in the native app's icon module at the same time, and
 * the two copies drifted by a single word boundary: this one bounded only the
 * right-hand side of `lift\b`, which "Forklift" satisfies, so the CRANE pattern
 * matched first and every forklift on the web was drawn as a tower crane. The
 * native app, which anchored both sides, drew it correctly. One rule, one place.
 *
 * What stays here is the mapping from the rule's answer to THIS platform's
 * drawing, which is the only part that is platform-specific. The motif names
 * and the icon names coincide today; the indirection is what keeps them free to
 * stop coinciding.
 */
const MOTIF_ICON: Record<MachineMotif, IconName> = {
  excavator: 'excavator',
  crane: 'crane',
  forklift: 'forklift',
  truck: 'truck',
  tractor: 'tractor',
  container: 'container',
  machine: 'machine',
};

export function machineMotif(title: string | null | undefined): IconName {
  return MOTIF_ICON[deriveMotif(title)];
}
